<script setup lang="ts">
  import { ref } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { ColoredButton, SearchBar, SearchToolbar, SelectInput, NoDocuments } from '@/shared/ui';
  import { useTableFilterModal } from '@/shared/lib';
  import { usePermission } from '@/entities/Permission';
  import type { TrainingContentFilters, TrainingContentListItem } from '@/entities/Training';
  import { useTrainingContentsSearch } from '../model/useTrainingContentsSearch';
  import { useTrainingContentsExport } from '../model/useTrainingContentsExport';
  import { useTrainingContentViewer } from '../model/useTrainingContentViewer';
  import { useDeleteTrainingContent } from '../model/useDeleteTrainingContent';
  import { CONTENT_QUANTITY_OPTIONS } from '../model/useTrainingContentsTable';
  import TrainingContentsTable from './TrainingContentsTable.vue';
  import TrainingContentFilterModal from './TrainingContentFilterModal.vue';
  import EditTrainingContentModal from './EditTrainingContentModal.vue';
  import CreateTrainingContentModal from './CreateTrainingContentModal.vue';

  import AddIcon from '@/shared/assets/icons/AddIcon.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';

  const {
    items,
    metadata,
    isInitialLoading,
    loadingMore,
    hasMoreItems,
    error,
    term,
    sort,
    order,
    itemsPerPage,
    showControls,
    isEmptyResult,
    filters,
    changePage,
    changeItemsPerPage,
    handleSearch,
    handleSortUpdate,
    applyFilters,
    clearFilters,
    fetch,
  } = useTrainingContentsSearch();

  const { can } = usePermission();

  const canCreateContent = can('readContent', 'Training');

  const { open: openCreateContentModal, close: closeCreateContentModal } = useModal({
    component: CreateTrainingContentModal,
    attrs: {
      onClose() {
        closeCreateContentModal();
      },
      onCreated() {
        fetch();
      },
    },
  });

  const { isFilterActive, openFilterModal } = useTableFilterModal<TrainingContentFilters>(
    TrainingContentFilterModal,
    { clearFilters, filters: {} },
    applyFilters,
    () => filters.value
  );

  const { isExporting, handleExportToExcel } = useTrainingContentsExport({
    term,
    sort,
    order,
    filters,
  });

  const { openContent } = useTrainingContentViewer();

  const { requestDelete } = useDeleteTrainingContent({ onDeleted: () => fetch() });

  const editingContentId = ref<number | null>(null);
  const isEditModalOpen = ref(false);

  const openEditModal = (content: TrainingContentListItem) => {
    editingContentId.value = content.id;
    isEditModalOpen.value = true;
  };

  const handleContentUpdated = () => {
    void fetch();
  };
</script>

<template>
  <div data-testid="training-contents-tab-page" class="flex flex-col">
    <div class="flex gap-3 mb-4">
      <ColoredButton
        v-if="canCreateContent"
        data-testid="btn-criar-conteudo"
        text="Criar Conteúdo"
        :icon="AddIcon"
        icon-alt="Criar conteúdo"
        @click="openCreateContentModal"
      />
      <ColoredButton
        v-if="showControls"
        :disabled="isEmptyResult || isExporting"
        data-testid="btn-exportar-excel"
        text="Exportar para Excel"
        :icon="ExportExcelIcon"
        icon-alt="Exportar para Excel"
        @click="handleExportToExcel"
      />
    </div>

    <div
      v-if="error"
      data-testid="training-contents-error-state"
      class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ error }}
    </div>

    <div
      v-else-if="isInitialLoading"
      data-testid="training-contents-loading-state"
      class="flex items-center justify-center py-12"
    >
      <span class="text-sm text-gray-05">Carregando conteúdos...</span>
    </div>

    <template v-else-if="showControls">
      <SearchToolbar class="mb-6">
        <template #search>
          <SearchBar
            data-testid="training-contents-search-bar"
            :initial-search-term="term"
            class="w-full"
            @search="handleSearch"
          />
        </template>
        <template #quantity>
          <SelectInput
            :model-value="itemsPerPage"
            :options="CONTENT_QUANTITY_OPTIONS"
            arrow-class="w-5 h-5"
            shadow-class="shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            @update:model-value="(value) => changeItemsPerPage(Number(value))"
          />
        </template>
      </SearchToolbar>

      <TrainingContentsTable
        :contents="items"
        :metadata="metadata"
        :items-per-page="itemsPerPage"
        :current-sort="sort"
        :current-order="order"
        :loading-more="loadingMore"
        :has-more-items="hasMoreItems"
        :is-filter-active="isFilterActive"
        @change-page="changePage"
        @update:sort="handleSortUpdate"
        @filter-clicked="openFilterModal"
        @view="openContent"
        @edit="openEditModal"
        @delete="requestDelete"
      />
    </template>

    <NoDocuments
      v-else
      data-testid="training-contents-empty-state"
      title="Nenhum conteúdo cadastrado"
    />

    <EditTrainingContentModal
      v-if="isEditModalOpen"
      v-model:is-open="isEditModalOpen"
      :content-id="editingContentId"
      @content-updated="handleContentUpdated"
    />
  </div>
</template>
